import { useCallback, useRef } from 'react';
import { FLOW_STATUS, FLOW_TYPE } from 'ts-components/Conversation';
import MachineLearningService from 'services/MachineLearningService';
import { useQuery } from '@tanstack/react-query';
import { CHAT_BOT } from 'queries/query-keys';

interface Context {
  [key: string]: any;
}

interface FlowStatusData {
  status?: string;
  run_metadata?: any;
}

interface UseSinglePromptConversationProps {
  existingFlowId: string;
  setExistingFlowId: (flowId: string) => void;
  flowType: keyof typeof FLOW_TYPE;
  customObjectId: string;
  context: Context;
  onStartGeneration: (flowId: string) => void;
  onGenerationSuccess: (flowStatusData: FlowStatusData) => void;
  onGenerationFailure: (error: any) => void;
}

export const useSinglePromptConversation = ({
  existingFlowId,
  setExistingFlowId,
  flowType,
  customObjectId,
  context,
  onStartGeneration,
  onGenerationSuccess,
  onGenerationFailure,
}: UseSinglePromptConversationProps) => {
  const generationFlowCountRef = useRef(0);

  const handleStartGeneration = useCallback(
    async (prompt: string) => {
      try {
        const startFlowResponse = await MachineLearningService.startFlow({
          type: flowType,
          options: {
            context,
            user_request: prompt,
            target_object_id: customObjectId,
          },
        });
        setExistingFlowId(startFlowResponse.flow_id);
        onStartGeneration(startFlowResponse.flow_id);
      } catch (error) {
        onGenerationFailure(error);
      }
    },
    [
      flowType,
      setExistingFlowId,
      customObjectId,
      context,
      onStartGeneration,
      onGenerationFailure,
    ]
  );

  const handleGenerationCompleted = useCallback(
    (flowStatusData: FlowStatusData = {}) => {
      if (
        flowStatusData?.status === FLOW_STATUS.FAILED ||
        !flowStatusData?.run_metadata
      ) {
        onGenerationFailure(flowStatusData);
      } else if (flowStatusData?.status === FLOW_STATUS.COMPLETED) {
        onGenerationSuccess(flowStatusData);
      }
    },
    [onGenerationSuccess, onGenerationFailure]
  );

  useQuery({
    queryKey: CHAT_BOT.FLOW_STATUS(
      FLOW_TYPE.FILTER_BUILDER_INLINE,
      customObjectId
    ),
    queryFn: async () => {
      const data = await MachineLearningService.getFlowStatus({
        flowType,
        objectId: customObjectId,
        flowId: existingFlowId,
      });
      generationFlowCountRef.current++;
      if (
        data.status === FLOW_STATUS.COMPLETED ||
        data.status === FLOW_STATUS.FAILED
      ) {
        handleGenerationCompleted(data);
        setExistingFlowId('');
      }
      return data;
    },
    enabled: Boolean(existingFlowId),
    refetchInterval: existingFlowId ? 500 : false,
    retry: 3,
  });

  return {
    existingFlowId,
    handleStartGeneration,
  };
};
